import { useState } from "react";
import axios from "axios";
import { PublicShell } from "@/components/PublicShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_COLOUR = {
  critical: "bg-rose-500 text-white",
  high: "bg-orange-500 text-white",
  medium: "bg-amber-400 text-amber-950",
  low: "bg-emerald-500 text-white",
  unknown: "bg-slate-300 text-slate-700",
};

const BAND_LABEL = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
  unknown: "Unknown",
};

const ROLES = ["CRO", "CPO", "CISO", "Head of Procurement", "Head of Third-Party Risk", "CFO", "Other"];

const SAMPLE = `# Paste one identifier per line.
# UK Companies House numbers (CRN) — usually 8 digits, sometimes prefixed (NI, SC, FC):
01777777
00445790
# Or full LEIs — 20 alphanumeric characters from GLEIF:
529900T8BM49AURSDO55
5493001KJTIIGC8Y1R12`;

// A CRN is 8 chars (digits or prefix+digits); a LEI is exactly 20 alphanumeric.
function classify(line) {
  const v = line.trim();
  if (!v || v.startsWith("#")) return null;
  if (v.length === 20 && /^[A-Z0-9]+$/i.test(v)) return { kind: "lei", value: v.toUpperCase() };
  if (v.length >= 6 && v.length <= 10 && /^[A-Z]{0,2}\d+$/i.test(v)) return { kind: "crn", value: v };
  return { kind: "unknown", value: v };
}

function parseInput(raw) {
  const lines = raw.split(/\r?\n/);
  const crns = [];
  const leis = [];
  const unknowns = [];
  for (const l of lines) {
    const c = classify(l);
    if (!c) continue;
    if (c.kind === "crn") crns.push(c.value);
    else if (c.kind === "lei") leis.push(c.value);
    else unknowns.push(c.value);
  }
  return { crns, leis, unknowns };
}

function BandPill({ band }) {
  const cls = BAND_COLOUR[band] || BAND_COLOUR.unknown;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}
      data-testid={`srl-band-${band}`}
    >
      {BAND_LABEL[band] || band}
    </span>
  );
}

function BandBar({ counts, total }) {
  if (!total) return null;
  const order = ["critical", "high", "medium", "low", "unknown"];
  return (
    <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
      {order.map((b) => {
        const pct = ((counts[b] || 0) / total) * 100;
        if (pct === 0) return null;
        return (
          <div
            key={b}
            style={{ width: `${pct}%` }}
            className={BAND_COLOUR[b].split(" ")[0]}
            title={`${BAND_LABEL[b]}: ${counts[b]}`}
            data-testid={`srl-bar-${b}`}
          />
        );
      })}
    </div>
  );
}

export default function SupplierRiskLens() {
  const [raw, setRaw] = useState(SAMPLE);
  const [organisation, setOrganisation] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Capture UTM params on mount — they're attribution that follows the
  // lead all the way through to the Owner hot-leads panel.
  const utm = (() => {
    if (typeof window === "undefined") return {};
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") || null,
      utm_campaign: p.get("utm_campaign") || null,
      utm_medium: p.get("utm_medium") || null,
    };
  })();

  const { crns, leis, unknowns } = parseInput(raw);
  const totalInput = crns.length + leis.length;

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      if (totalInput === 0) {
        setError("Paste at least one Companies House number or LEI.");
        setSubmitting(false);
        return;
      }
      const { data } = await axios.post(`${API}/public/supplier-risk-lens/score`, {
        company_numbers: crns,
        leis,
        organisation: organisation || null,
        role: role || null,
        email: email || null,
        ...utm,
      });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.[0]?.msg || "Lookup failed — please try again",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const s = result.scorecard;
    const failing = s.dissolved_or_failing_count;
    const successRate = s.total_requested
      ? Math.round((s.lookups_succeeded / s.total_requested) * 100)
      : 0;
    return (
      <PublicShell active="/supplier-risk-lens">
        <div className="bg-gradient-to-br from-slate-50 to-violet-50 px-6 py-12 pb-40">
          <div className="mx-auto max-w-4xl">
            <div
              className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-violet-100"
              data-testid="srl-scorecard"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold">
                EKSELENS · ERIO · Supplier Risk Lens
              </p>
              <div className="mt-3 flex items-baseline gap-4">
                <h1 className="text-4xl font-bold text-slate-900">
                  {s.lookups_succeeded}
                  <span className="text-slate-400 text-2xl">/{s.total_requested}</span>
                </h1>
                <span className="text-sm text-slate-600">
                  suppliers resolved · {successRate}% success rate
                </span>
              </div>

              <div className="mt-6">
                <BandBar counts={s.band_counts} total={s.lookups_succeeded} />
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600">
                  {Object.entries(s.band_counts).map(([b, n]) =>
                    n > 0 ? (
                      <span key={b} className="flex items-center gap-1.5">
                        <span
                          className={`h-2 w-2 rounded-full ${BAND_COLOUR[b].split(" ")[0]}`}
                        />
                        {BAND_LABEL[b]}: <strong>{n}</strong>
                      </span>
                    ) : null,
                  )}
                </div>
              </div>

              {failing > 0 && (
                <div
                  className="mt-6 rounded-lg bg-rose-50 px-4 py-3 ring-1 ring-rose-200"
                  data-testid="srl-failing-banner"
                >
                  <p className="text-sm text-rose-900">
                    <strong>{failing}</strong> suppliers show critical or high
                    distress signals (dissolved, in administration, or lapsed
                    registration). These warrant immediate review.
                  </p>
                </div>
              )}

              {!result.registries.companies_house.enabled && crns.length > 0 && (
                <div
                  className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-xs text-amber-900 ring-1 ring-amber-200"
                  data-testid="srl-ch-disabled-banner"
                >
                  Companies House lookups returned an error — the operator
                  needs to register a free API key at
                  developer.company-information.service.gov.uk to enable UK
                  CRN lookups. GLEIF (LEI) lookups continue to work.
                </div>
              )}
            </div>

            <div
              className="mt-6 rounded-2xl bg-white p-6 shadow ring-1 ring-slate-200"
              data-testid="srl-results-list"
            >
              <h2 className="text-base font-semibold text-slate-900">
                Per-supplier breakdown
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                All lookups use public registries (Companies House, GLEIF). No
                vendor licence required, no proprietary data redistributed.
              </p>
              <ul className="mt-4 divide-y divide-slate-100">
                {result.results.map((r, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between py-3"
                    data-testid={`srl-result-row-${idx}`}
                  >
                    <div className="min-w-0 flex-1 pr-4">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {r.ok
                          ? r.supplier?.legal_name || "(no name)"
                          : <span className="text-slate-500">Lookup failed</span>}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        <span className="font-mono">{r.input}</span>
                        <span className="mx-1.5">·</span>
                        {r.source === "companies_house" ? "Companies House" : "GLEIF (LEI)"}
                        {r.ok && r.supplier?.country && (
                          <>
                            <span className="mx-1.5">·</span>
                            {r.supplier.country}
                          </>
                        )}
                      </p>
                      {r.ok && r.warnings?.length > 0 && (
                        <p className="mt-1 text-[11px] text-amber-700">
                          {r.warnings.slice(0, 2).join(" · ")}
                          {r.warnings.length > 2 && ` · +${r.warnings.length - 2} more`}
                        </p>
                      )}
                      {!r.ok && (
                        <p className="mt-1 text-[11px] text-rose-600">{r.error}</p>
                      )}
                    </div>
                    {r.ok ? (
                      <BandPill band={r.supplier?.financial_distress?.band || "unknown"} />
                    ) : (
                      <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-500">
                        n/a
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  setResult(null);
                  setError("");
                }}
                className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                data-testid="srl-run-again-btn"
              >
                Run again with a different list
              </button>
              {result.pdf_available && result.lens_id && (
                <a
                  href={`${API}/public/supplier-risk-lens/${result.lens_id}/pdf`}
                  className="rounded-lg border border-violet-300 bg-violet-50 px-5 py-2.5 text-sm font-medium text-violet-700 hover:bg-violet-100"
                  data-testid="srl-pdf-download-btn"
                >
                  Download PDF summary
                </a>
              )}
              <a
                href="/pilot"
                className="rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-violet-700"
                data-testid="srl-pilot-cta"
              >
                Book a pilot to see live supplier intelligence →
              </a>
            </div>
          </div>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell active="/supplier-risk-lens">
      <div className="bg-gradient-to-br from-slate-50 to-violet-50 px-6 py-12 pb-40">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-violet-100">
            <p className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold">
              EKSELENS · ERIO · Free tool
            </p>
            <h1 className="mt-2 text-3xl font-bold text-slate-900">
              UK Supplier Risk Lens
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Drop in up to 25 UK Companies House numbers or LEIs.
              ERIO resolves each against the public registry (Companies House
              for CRNs, GLEIF for LEIs) and returns a vendor-neutral risk band
              breakdown — the same canonical model we use inside the product.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              No vendor data redistributed. No D&B / Creditsafe licence needed.
              Public-data only. Rate-limited (10/min anon).
            </p>

            <div className="mt-6">
              <label
                htmlFor="srl-input"
                className="text-sm font-medium text-slate-700"
              >
                Supplier identifiers
              </label>
              <textarea
                id="srl-input"
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                className="mt-2 h-44 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs text-slate-900 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                data-testid="srl-input-textarea"
                spellCheck={false}
              />
              <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                <span data-testid="srl-crn-count">
                  CRNs: <strong className="text-slate-900">{crns.length}</strong>
                </span>
                <span data-testid="srl-lei-count">
                  LEIs: <strong className="text-slate-900">{leis.length}</strong>
                </span>
                {unknowns.length > 0 && (
                  <span className="text-amber-700" data-testid="srl-unknown-count">
                    Unrecognised: <strong>{unknowns.length}</strong>
                  </span>
                )}
                {totalInput > 25 && (
                  <span className="text-rose-600">
                    Max 25 per run — extras will be trimmed
                  </span>
                )}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Organisation (optional)
                </label>
                <input
                  type="text"
                  value={organisation}
                  onChange={(e) => setOrganisation(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  placeholder="e.g. Acme PLC"
                  data-testid="srl-organisation"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Role (optional)
                </label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  data-testid="srl-role"
                >
                  <option value="">Choose a role…</option>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700">
                  Email — to receive a PDF copy of the results (optional)
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  placeholder="you@company.com"
                  data-testid="srl-email"
                />
              </div>
            </div>

            {error && (
              <div
                className="mt-4 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-800 ring-1 ring-rose-200"
                data-testid="srl-error"
              >
                {error}
              </div>
            )}

            <div className="mt-6 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                We never share your input with any third party. Lookups query
                Companies House and GLEIF directly.
              </p>
              <button
                type="button"
                onClick={submit}
                disabled={submitting || totalInput === 0}
                className="rounded-lg bg-violet-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="srl-submit-btn"
              >
                {submitting ? "Looking up…" : `Score ${totalInput || 0} suppliers`}
              </button>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">
            Want to see live supplier intelligence, drift detection, and £
            exposure across your portfolio?{" "}
            <a
              href="/pilot"
              className="font-medium text-violet-600 hover:underline"
              data-testid="srl-bottom-cta"
            >
              Book a pilot →
            </a>
          </p>
        </div>
      </div>
    </PublicShell>
  );
}
